/**
 * What the service worker does with a request, as a pure function.
 *
 * Pulled out of `src/sw.ts` and unit-tested because a service worker is the
 * least observable code in this app: it runs off the page, outlives the tab
 * that installed it, and neither `bun run typecheck`'s DOM lib nor any test
 * harness here can exercise its lifecycle. The DECISIONS can be tested, so they
 * are, and `sw.ts` is left as the thin part that only wires them to caches.
 *
 * Two rules in here are the reason it exists at all, and both have a test named
 * after them:
 *
 *   THE OVERLAY IS NEVER TOUCHED. `/t/<slug>/overlay/:field` (and its legacy
 *   address `/overlay/:field`) is what OBS renders on stream. A stale shell
 *   there is an on-air failure, so it is refused first, before any other
 *   consideration, and regardless of the worker's scope.
 *
 *   THE API IS AN ALLOWLIST. Only reads that are public AND carry no
 *   credential are stored. `/api/session` is `{ need: "public" }` in the route
 *   table and still must never be cached: it describes which credentials this
 *   browser holds.
 */

export type SwRoute =
  /** Not ours. Do not call `respondWith` at all; let the network have it. */
  | "bypass"
  /** An HTML navigation: network first, the precached shell if that fails. */
  | "shell"
  /** A build asset: content-hashed, so the precache is authoritative. */
  | "asset"
  /** An allowlisted public read: network first with a short patience window;
   *  the LAST stored answer only when the network loses that race or fails
   *  (`data()` in `sw.ts`). Online, nothing is served stale. */
  | "data";

/**
 * The OBS overlay, at either of its addresses. Shared with `main.tsx`, which
 * neither registers the worker from this surface nor mounts a toast host on
 * it: one predicate, so the two can never disagree about what the overlay is.
 */
export function isOverlayPath(pathname: string): boolean {
  return /^\/overlay\//.test(pathname) || /^\/t\/[^/]+\/overlay\//.test(pathname);
}

/**
 * Public GET endpoints whose last answer is worth keeping, as the part of the
 * path AFTER the tournament prefix.
 *
 * This is the list of reads the BOARD makes (`src/lib/queries.ts`), which is
 * what "last known scores stay visible" means. It is an allowlist rather than
 * "anything under /api that is a GET" on purpose, and mirrors the greppable
 * `need: "public"` idiom the route table uses (`worker/index.ts`): adding an
 * endpoint here has to be a decision somebody made, not a default they
 * inherited. Every one exists at `/api/t/<id>/<read>` and, as the legacy alias
 * for the event `main`, at `/api/<read>` (V2 Phase 1); both forms are kept.
 */
const TOURNAMENT_READS = [
  // The board's one-request bundle (shared/board.ts). Without it here, the
  // installed board's dead-zone reload would paint an empty shell: this is
  // now THE read that surface makes.
  "board",
  "teams",
  "schedule",
  "fields",
  "pools",
  "divisions",
  "stats",
  "settings",
  "persons",
  "awards/public",
  "spirit/public",
] as const;

/** `field/<id>/state` — the REST read behind each field's live socket. */
const FIELD_STATE = /^field\/[A-Za-z0-9_-]{1,32}\/state$/;

/** A tournament id as a path segment: a slug, or `main`. The same shape the
 *  router's `isTournamentId` accepts; anything else is not ours to store. */
const TOURNAMENT_SEGMENT = /^[a-z0-9][a-z0-9-]{2,39}$/;

/**
 * The registry reads the front page and the `/t/$slug` layout make. Both are
 * public and carry nothing personal: the list of published events, and one
 * event by id or slug.
 */
function isRegistryRead(pathname: string): boolean {
  if (pathname === "/api/tournaments") return true;
  const m = /^\/api\/tournaments\/([^/]+)$/.exec(pathname);
  return m !== null && m[1] !== undefined && TOURNAMENT_SEGMENT.test(m[1]);
}

/** The tournament-scoped reads, at either address. */
function isTournamentRead(pathname: string): boolean {
  const m = /^\/api(?:\/t\/([^/]+))?\/(.+)$/.exec(pathname);
  if (m === null || m[2] === undefined) return false;
  if (m[1] !== undefined && !TOURNAMENT_SEGMENT.test(m[1])) return false;
  const rest = m[2];
  return (TOURNAMENT_READS as readonly string[]).includes(rest) || FIELD_STATE.test(rest);
}

export interface SwRequest {
  /** Same-origin pathname. Query and hash are deliberately not consulted. */
  pathname: string;
  method: string;
  /** True for a document request (`request.mode === "navigate"`). */
  navigate: boolean;
  /** False for anything off this origin: fonts, none today, but never ours. */
  sameOrigin: boolean;
}

export function swRoute(req: SwRequest): SwRoute {
  // FIRST, and before anything else. The bypass has to hold even if a future
  // change registers the worker from somewhere new, so it is a property of the
  // URL rather than of where `register()` was called.
  if (isOverlayPath(req.pathname)) return "bypass";

  // Mutations never meet this file: the CSRF triad, the session cookies and
  // the audit entry are all the Worker's business, and a cache has no
  // business replaying a POST.
  if (req.method !== "GET") return "bypass";
  if (!req.sameOrigin) return "bypass";

  if (req.pathname.startsWith("/api/")) {
    return isRegistryRead(req.pathname) || isTournamentRead(req.pathname) ? "data" : "bypass";
  }

  // Vite content-hashes everything under /assets, so a changed file has a
  // changed name and a cache hit is by definition the right answer.
  if (req.pathname.startsWith("/assets/")) return "asset";

  if (req.navigate) return "shell";
  return "bypass";
}
